import {
  Button,
  GenericObjectCard as ToolkitGenObjCard,
  YesObject
} from '@yes.technology/react-toolkit'
import { Link } from 'react-router-dom'

import CrazyCard from 'component/CrazyCard'
import ImageDocument from 'component/ImageDocument/ImageDocument'
import React from 'react'
import { DataCardsButtonProps } from 'site/Renderer/components/DataCards/DataCards.types'
import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'

import { useFeatureFlag } from 'featureFlags/useFeatureFlag'
import { useLocales } from 'locales/hooks'
import {
  ActionButtonEnum,
  ActionButtonProps,
  ActionButtonType,
  SitemodelContent
} from './GenericObjectCard.types'
import GenericObjectCardButton from './GenericObjectCardButton'
import GenericObjectCardContentWrapper, {
  GenericObjectCardContentWrapperProps
} from './GenericObjectCardContentWrapper'

export type GenericObjectCardProps = Pick<YesObject, 'des' | 'complement'> &
  Partial<Pick<YesObject, 'sitemodel_content' | 'Document'>> & {
    CustomAction?: React.ReactNode
    buttonProps?: DataCardsButtonProps
    customWrapperProps?: GenericObjectCardContentWrapperProps['wrapperProps']
    wrapperType?: GenericObjectCardContentWrapperProps['wrapperType']
    enableContentFading?: boolean
    complementLines?: number
  } & Partial<
    Pick<
      React.ComponentProps<typeof ToolkitGenObjCard>,
      'imageFormat' | 'imageHeight' | 'frameImage'
    >
  >

const GenericObjectCard = ({
  des,
  complement,
  sitemodel_content: sitemodelContent,
  Document: image,
  CustomAction = null,
  buttonProps,
  imageFormat,
  imageHeight,
  frameImage,
  customWrapperProps,
  wrapperType = 'Fragment',
  enableContentFading = false,
  complementLines = 0
}: GenericObjectCardProps) => {
  const { getSitemodelByLocale } = useLocales()
  const workflowmodelExecutorUuid = getSitemodelByLocale({
    demand: 'workflowmodelExecutorUuid'
  })

  const processmodelExecutorUuid = getSitemodelByLocale({
    demand: 'processmodelExecutorUuid'
  })

  const [isProcessmodelEnabled] = useFeatureFlag('processmodels')

  const JSONContent =
    typeof sitemodelContent === 'string' ? getJSONContent(sitemodelContent) : {}

  const actionButtons = getActionButtons(JSONContent)
  const previewComponents = getPreviewComponents(JSONContent)
  const defaultButton = getButtonByType(actionButtons, ActionButtonEnum.default)
  const workflowmodelButton = getButtonByType(
    actionButtons,
    ActionButtonEnum.workflowmodel
  )
  const helptexttButton = getButtonByType(
    actionButtons,
    ActionButtonEnum.helptext
  )
  const linkButton = getButtonByType(actionButtons, ActionButtonEnum.link)
  const crazyButton = getButtonByType(actionButtons, ActionButtonEnum.oneclick)
  const href = linkButton?.action || '#'

  const imageForwardProp = image && <ImageDocument documents={image} />
  const buttonForwardProp = defaultButton ? (
    <Link
      to={`/${defaultButton.action}`}
      style={{ width: '100%', textDecoration: 'none' }}
    >
      <Button
        style={{ fontSize: '12px' }}
        heightClass='semiSmall'
        variant='secondary'
      >
        {defaultButton.label}
      </Button>
    </Link>
  ) : undefined

  const cardWrapperType =
    previewComponents && previewComponents.length > 0
      ? 'Accordion'
      : wrapperType

  const actionLinkUrl = isProcessmodelEnabled
    ? `/${processmodelExecutorUuid}?processmodelUuid=${workflowmodelButton?.action}`
    : `/${workflowmodelExecutorUuid}?workflowmodelUuid=${workflowmodelButton?.action}`

  if (crazyButton)
    return (
      <GenericObjectCardContentWrapper
        wrapperType={cardWrapperType}
        wrapperProps={customWrapperProps}
        components={previewComponents}
      >
        <CrazyCard
          des={crazyButton?.label}
          workflowmodeluuid={crazyButton?.action}
        />
      </GenericObjectCardContentWrapper>
    )

  return (
    <GenericObjectCardContentWrapper
      wrapperType={cardWrapperType}
      wrapperProps={customWrapperProps}
      components={previewComponents}
    >
      <ToolkitGenObjCard
        des={<SafeHtmlContent html={des} />}
        complement={<SafeHtmlContent html={complement} />}
        image={imageForwardProp}
        imageFormat={imageFormat}
        imageHeight={imageHeight}
        frameImage={frameImage}
        defaultButton={buttonForwardProp}
        fullHeight
        applyFadingOverTitle={enableContentFading}
        complementLines={complementLines}
      >
        {workflowmodelButton && (
          <GenericObjectCardButton
            buttonProps={buttonProps}
            href={actionLinkUrl}
            label={workflowmodelButton?.label}
            variant='primary'
            enableFading={enableContentFading}
          />
        )}
        {helptexttButton && (
          <GenericObjectCardButton
            buttonProps={buttonProps}
            href={`/${helptexttButton?.action}`}
            label={helptexttButton?.label}
            variant='secondary'
            enableFading={enableContentFading}
          />
        )}
        {linkButton && (
          <GenericObjectCardButton
            buttonProps={buttonProps}
            href={href}
            label={linkButton?.label}
            variant='secondary'
            linkTarget='_blank'
            enableFading={enableContentFading}
          />
        )}

        {CustomAction}
      </ToolkitGenObjCard>
    </GenericObjectCardContentWrapper>
  )
}

const getJSONContent = (jsonString: string) => {
  const json = JSON.parse(jsonString)
  return json
}

const getActionButtons = (content: SitemodelContent) => {
  return content.action_buttons
}

const getPreviewComponents = (content: SitemodelContent) => {
  return content.card_preview_components
}

const getButtonByType = (
  actionButtons: ActionButtonProps[] | undefined,
  type: ActionButtonType
): ActionButtonProps | null => {
  if (!actionButtons) return null
  const [first] = actionButtons?.filter((button) => button.type === type)
  return first || null
}

export default GenericObjectCard
